import {
  TIME_FORMATS,
  FORMAT_MS,
  SECOND_TO_DAY_END,
  FORMAT_YMD_HHMMSS
} from '@/components/shared/date-format';
import { mapState } from 'vuex';
import MomentTimezone from 'moment-timezone';

const NO_FEEDER_NAME = '';

const TIMESPAN = {
  DAY: {
    VALUE: 0,
    TICK_PIXEL_INTERVAL: 36 * 3,
    FORMAT: {
      day: TIME_FORMATS.HOUR_MINUTE,
      hour: TIME_FORMATS.HOUR_MINUTE,
      minute: TIME_FORMATS.HOUR_MINUTE
    }
  },
  LAST_24: {
    VALUE: 1,
    TICK_PIXEL_INTERVAL: 36 * 3,
    FORMAT: {
      day: TIME_FORMATS.HOUR_MINUTE,
      hour: TIME_FORMATS.HOUR_MINUTE,
      minute: TIME_FORMATS.HOUR_MINUTE
    }
  },
  WEEK: {
    VALUE: 7,
    TICK_PIXEL_INTERVAL: 36 * 5,
    FORMAT: {
      day: TIME_FORMATS.NAMED_DAY,
      hour: TIME_FORMATS.DAY_MONTH_HOUR_MINUTE,
      minute: TIME_FORMATS.DAY_MONTH_HOUR_MINUTE
    }
  },
  MULTIPLE: {
    TICK_PIXEL_INTERVAL: 36,
    FORMAT: {
      month: TIME_FORMATS.DAY_MONTH,
      day: TIME_FORMATS.DAY_MONTH,
      hour: TIME_FORMATS.DAY_MONTH_HOUR_MINUTE,
      minute: TIME_FORMATS.DAY_MONTH_HOUR_MINUTE
    }
  }
};
const last24HourMinMax = {
  min: parseInt(
    MomentTimezone()
      .subtract(1, 'day')
      .format(FORMAT_MS),
    10
  ),
  max: parseInt(MomentTimezone().format(FORMAT_MS), 10)
};

export default {
  computed: mapState({
    projects: state => state.projects.projects
  }),
  methods: {
    generateChart(chartOptions = {}) {
      const defaultChartOptions = {
        chart: {
          styledMode: false,
          type: 'line',
          height: '700px',
          redraw: true,
          animation: false,
          zoomType: 'xy',
          panning: true,
          panKey: 'shift',
          backgroundColor: null
        },
        time: {
          timezoneOffset: -1 * MomentTimezone().utcOffset()
        },
        boost: { enabled: true, allowForce: true, seriesThreshold: Infinity },
        tooltip: {
          formatter() {
            const time = MomentTimezone(this.x).format('YYYY-MM-DD HH:mm:ss');
            const { name } = this.series;
            const { color } = this.series;
            const unit = this.series.userOptions.suffix;
            return `<span style="color:${color}">\u25CF</span>
              <b>${name}</b><br />
              value: ${this.y} <i>[${unit}]</i><br />
              time: ${time}`;
          }
        },
        area: { cropThreshold: 500 }
      };

      return { ...defaultChartOptions, ...chartOptions };
    },
    selectTimeOptions(option) {
      switch (true) {
        case option === TIMESPAN.DAY.VALUE:
          return {
            tickInterval: TIMESPAN.DAY.TICK_PIXEL_INTERVAL,
            dateFormat: TIMESPAN.DAY.FORMAT
          };
        case option === TIMESPAN.LAST_24.VALUE:
          return {
            tickInterval: TIMESPAN.DAY.TICK_PIXEL_INTERVAL,
            dateFormat: TIMESPAN.DAY.FORMAT
          };
        case option === TIMESPAN.WEEK.VALUE:
          return {
            tickInterval: TIMESPAN.WEEK.TICK_PIXEL_INTERVAL,
            dateFormat: TIMESPAN.WEEK.FORMAT
          };
        default:
          return {
            tickInterval: TIMESPAN.MULTIPLE.TICK_PIXEL_INTERVAL,
            dateFormat: TIMESPAN.MULTIPLE.FORMAT
          };
      }
    },
    createXAxis(options) {
      const { startDate, endDate, span } = options;
      const { dateFormat, tickInterval } = this.selectTimeOptions(span);

      const axises =
        span === TIMESPAN.LAST_24.VALUE
          ? last24HourMinMax
          : {
              min: parseInt(
                MomentTimezone(startDate)
                  .startOf('day')
                  .format(FORMAT_MS),
                10
              ),
              max: parseInt(
                MomentTimezone(endDate)
                  .add(SECOND_TO_DAY_END, 'seconds')
                  .format(FORMAT_MS),
                10
              )
            };
      return {
        title: { text: '<b>Time<b>' },
        type: 'datetime',
        ...axises,
        tickPixelInterval: tickInterval,
        dateTimeLabelFormats: dateFormat
      };
    },
    generatePhaseFeederTooltip(feederNames) {
      return {
        formatter() {
          const time = MomentTimezone(this.x).format(FORMAT_YMD_HHMMSS);
          const { name } = this.series;
          const { color } = this.series;
          const unit = this.series.userOptions.suffix;
          const foundFeeder = feederNames.find(
            feederInfo => feederInfo.number === this.series.userOptions.feederNumber
          );
          const feederName = !foundFeeder ? NO_FEEDER_NAME : foundFeeder.name;

          return `<span style="color:${color}">\u25CF</span>
            <b>${name}</b><br />
            value: ${this.y} <i>[${unit}]</i><br />
            time: ${time} <br />
            ${feederName}`;
        }
      };
    },
    getBase(val) {
      return Math.pow(10, String(Math.ceil(val)).length - 1);
    },
    roundUp(val) {
      const base = this.getBase(val);

      return Math.ceil(val / base) * base;
    },
    generateArrayOfTicks(step) {
      return [-3, -2, -1, 0, 1, 2, 3].map(value => Math.round(value * step));
    }
  }
};
